import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import AuthModal from "@/components/AuthModal";
import CreatorStats from "@/components/CreatorStats";
import StreakBadge from "@/components/StreakBadge";
import ReferralCard from "@/components/ReferralCard";
import RugProofBadge from "@/components/RugProofBadge";
import { Eye, Heart, Coins, Sparkles, Star, ArrowRight, Loader2, Zap, Flame } from "lucide-react";

const STATUS_STYLE = {
  draft: "border-[#ffd700]/40 text-[#ffd700]",
  published: "border-[#00ff41]/40 text-[#00ff41]",
  featured: "border-[#cfff04] text-[#cfff04] bg-[#cfff04]/10",
};

function CoinRow({ c, onPromo, promoBusy }) {
  const solTips = ((c.tips_total_lamports || 0) / 1_000_000_000).toFixed(2);
  return (
    <div
      className="border border-[#3a3a3a] bg-[#121212] p-5 grid grid-cols-12 gap-4 items-center hover:bg-[#1a1a1a] transition-colors"
      data-testid={`my-coin-row-${c.slug}`}
    >
      <div className="col-span-12 sm:col-span-5 flex items-center gap-4 min-w-0">
        <div className="w-12 h-12 flex-shrink-0 border border-[#3a3a3a] bg-[#050505] overflow-hidden">
          <img
            src={`${BACKEND_URL}/api/coins/${c.slug}/logo`}
            alt={c.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold uppercase truncate">{c.name}</div>
          <div className="font-mono text-xs text-[#e2e2e2]">
            <span className="text-[#cfff04]">${c.ticker}</span> · {c.theme}
          </div>
        </div>
      </div>
      <div className="col-span-6 sm:col-span-3 flex items-center gap-4 font-mono text-[11px] uppercase tracking-wider text-[#d0d0d0]">
        <span
          className={`border px-2 py-1 ${STATUS_STYLE[c.status] || "border-[#333] text-[#e2e2e2]"}`}
          data-testid={`my-coin-status-${c.slug}`}
        >
          {c.status === "featured" && <Star size={9} className="inline mr-1 -mt-0.5" fill="currentColor" />}
          {c.status}
        </span>
        {c.mint_address && (
          <span
            className="border border-[#00ff41]/50 text-[#00ff41] px-2 py-1"
            data-testid={`my-coin-onchain-${c.slug}`}
          >
            on-chain
          </span>
        )}
        <RugProofBadge coin={c} compact />
        {c.boost_nudge && (
          <span
            className="border border-[#FF3E9D]/60 text-[#FF3E9D] px-2 py-1 inline-flex items-center gap-1 animate-pulse"
            data-testid={`boost-nudge-${c.slug}`}
          >
            <Flame size={9} /> {c.views_7d} views — boost it
          </span>
        )}
        <span className="flex items-center gap-1"><Eye size={11} /> {c.views || 0}</span>
        <span className="flex items-center gap-1"><Heart size={11} /> {c.likes || 0}</span>
        <span>{solTips} SOL</span>
      </div>
      <div className="col-span-6 sm:col-span-4 flex sm:justify-end items-center gap-2 flex-wrap">
        <Link
          to={c.status === "draft" ? `/create?resume=${c.slug}` : `/c/${c.slug}`}
          data-testid={`my-coin-view-${c.slug}`}
          className="border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors"
        >
          {c.status === "draft" ? "Resume →" : "View →"}
        </Link>
        {c.status === "published" && (
          <>
            <button
              onClick={() => onPromo(c.slug, "BOOST_24H")}
              disabled={promoBusy === `${c.slug}:BOOST_24H`}
              data-testid={`my-coin-boost-${c.slug}`}
              className="btn-brutal bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-[11px] px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {promoBusy === `${c.slug}:BOOST_24H` ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
              Boost $9
            </button>
            <button
              onClick={() => onPromo(c.slug, "FEATURE_7D")}
              disabled={promoBusy === `${c.slug}:FEATURE_7D`}
              data-testid={`my-coin-feature-${c.slug}`}
              className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-[11px] px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {promoBusy === `${c.slug}:FEATURE_7D` ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              Feature $49
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function MyCoins() {
  const { user, loading: authLoading } = useAuth();
  const [coins, setCoins] = useState(null); // null = loading
  const [promoBusy, setPromoBusy] = useState(null); // "slug:sku" being checked out
  const [authOpen, setAuthOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/coins");
      setCoins(data.coins);
    } catch {
      setCoins([]);
      toast.error("Couldn't load your coins — try refreshing");
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onPromo = async (slug, sku) => {
    setPromoBusy(`${slug}:${sku}`);
    try {
      const { data } = await api.post("/payments/checkout", {
        sku,
        coin_slug: slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setPromoBusy(null);
      toast.error(e.response?.data?.detail || "Payment failed — try again");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="my-coins-page">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
            // Creator dashboard
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-8">
            My coins.
          </h1>

          {(() => {
            if (authLoading) {
              return <div className="border border-[#3a3a3a] bg-[#121212] h-40 animate-pulse" />;
            }
            if (!user) {
              return (
            <div className="border border-[#3a3a3a] bg-[#121212] p-10 text-center" data-testid="my-coins-signin-prompt">
              <Coins size={32} className="mx-auto text-[#cfff04] mb-4" />
              <p className="font-display text-2xl font-bold uppercase mb-2">Sign in to see your coins</p>
              <p className="text-sm text-[#e2e2e2] mb-6 max-w-sm mx-auto">
                Coins you create while signed in are linked to your account — track views, likes, and SOL tips here.
              </p>
              <button
                onClick={() => setAuthOpen(true)}
                data-testid="my-coins-signin-btn"
                className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4"
              >
                Sign in
              </button>
              <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
            </div>
              );
            }
            if (coins === null) {
              return (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={`mycoins-skeleton-${i}`} className="border border-[#3a3a3a] bg-[#121212] h-20 animate-pulse" />
              ))}
            </div>
              );
            }
            if (coins.length === 0) {
              return (
            <div className="border border-[#cfff04] bg-[#121212] p-10 text-center" data-testid="my-coins-empty">
              <p className="font-display text-3xl font-black uppercase tracking-tighter mb-3">
                No coins yet.
              </p>
              <p className="text-sm text-[#e2e2e2] mb-6 max-w-md mx-auto">
                Launch one in 60 seconds — it&apos;ll appear here automatically since you&apos;re signed in.
              </p>
              <Link
                to="/create"
                data-testid="my-coins-launch-cta"
                className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4 items-center gap-2"
              >
                <Sparkles size={16} /> Launch your first coin
              </Link>
            </div>
              );
            }
            return (
            <>
              <CreatorStats />
              <StreakBadge />
              <div className="space-y-3" data-testid="my-coins-list">
                {coins.map((c) => (
                  <CoinRow key={c.slug} c={c} onPromo={onPromo} promoBusy={promoBusy} />
                ))}
              </div>
              <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
                <p className="font-mono text-[11px] text-[#d0d0d0] uppercase tracking-widest">
                  // {coins.length} coin{coins.length === 1 ? "" : "s"} linked to {user.email}
                </p>
                <Link
                  to="/create"
                  data-testid="my-coins-new-cta"
                  className="font-mono text-xs uppercase tracking-widest text-[#cfff04] hover:underline inline-flex items-center gap-1"
                >
                  Launch another <ArrowRight size={12} />
                </Link>
              </div>
            </>
            );
          })()}
          {!authLoading && user && coins !== null && <ReferralCard />}
        </div>
      </main>
      <Footer />
    </div>
  );
}
